import type { Middleware } from 'openapi-fetch';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { queryClient } from '@/lib/react-query/queryClient';
import { authEndpoints } from '@/modules/auth/constants';
import { usersQueryKeys } from '@/modules/users/queries/queryKeys';
import type { CurrentUserApi } from '@/modules/users/types';

import { createSessionRefresh, withCrossTabLock } from './sessionRefresh';

export const authMiddleware: Middleware = {
  async onRequest({ request }: { request: Request }) {
    if (isAuthEndpoint(request)) {
      return request;
    }

    const currentUser = queryClient.getQueryData<CurrentUserApi>(
      usersQueryKeys.current(),
    );

    if (!currentUser) {
      return request;
    }

    // TODO: Add tenant ID to the request when the API supports it
    // const tenantId = currentUser.tenantIds?.[0];
    // invariant(tenantId, 'Tenant ID is required');

    // request.headers.set('Current-Tenant-Id', tenantId);
    return request;
  },
  async onResponse({
    request,
    response,
  }: {
    request: Request;
    response: Response;
  }) {
    // Don't refresh token if the request is an auth endpoint or the response is not 401
    if (isAuthEndpoint(request) || response.status !== 401) {
      return response;
    }

    // Refresh the token and retry the original request if that succeeded.
    if (await refreshSession()) {
      // Retry the original request using plain fetch to avoid middleware recursion
      // The request will inherit credentials from the original fetchClient configuration
      return fetch(request);
    }

    return response;
  },
};

const refreshSession = createSessionRefresh({
  refresh: () => fetchClient.POST(authEndpoints.refreshToken),
  onSessionLost: () => clearCurrentUser(),
  withLock: withCrossTabLock,
});

// The session is gone and cannot be refreshed. The current-user query never
// refetches on focus and is cached for an hour, so without this every other
// query would see a 401 while `ProtectedRoute` and `Login` still trusted the
// cached user, bouncing the router between `/login` and the protected route
// forever. Mirror `useLogoutMutation` and null the entry so every consumer
// agrees the user is signed out.
const clearCurrentUser = () => {
  const userQueryKey = usersQueryKeys.current();
  if (queryClient.getQueryData(userQueryKey)) {
    queryClient.setQueryData(userQueryKey, null);
  }
};

const isAuthEndpoint = (url: Request) => {
  const pathname = getPathname(url);
  return Object.values(authEndpoints).some((endpoint) =>
    pathname.endsWith(endpoint),
  );
};

const getPathname = (request: Request) => {
  try {
    return new URL(request.url).pathname;
  } catch {
    return request.url;
  }
};
